import React from 'react';
import PropTypes from 'prop-types';
import { DropdownMenu, SegmentedControl, SparkIcon } from '@spark';
import FieldLabel from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/FieldLabel';
import TextAreaField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/TextAreaField';
import RecordCountInput from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/RecordCountInput';
import NerExampleEditor from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/NerExampleEditor';
import { NER_EXAMPLES_INSTRUCTION } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import {
    EXAMPLE_MODE_ITEMS,
    GENERATIVE_SETTINGS_HINT,
    HELP_TEXTS,
} from '@/features/datasets/components/DataCollection/RetrievalAgent/constants';

const LABEL_DESCRIPTION_PLACEHOLDER =
    'e.g., Use when the user expresses satisfaction, approval, or a positive experience.';

const entityDescriptionPlaceholder = (name) => `e.g., Describe what a \u201c${name}\u201d entity should include.`;

const EXAMPLE_TEXTAREA_HEIGHT = 95;

const RemoveExampleButton = ({ index, onRemove }) => (
    <button
        type="button"
        className="gdai-icon-button"
        aria-label={`Remove example ${index + 1}`}
        onClick={onRemove}
    >
        <SparkIcon slug="trash" size="small" />
    </button>
);

RemoveExampleButton.propTypes = {
    index: PropTypes.number.isRequired,
    onRemove: PropTypes.func.isRequired,
};

/** Classification example: text + Label / Labels select. */
const ClassificationExampleRow = ({ example, index, labelNames, isMultilabel, onChange, onRemove }) => {
    const selectedLabels = example.labels || [];
    return (
        <div className="gdai-section">
            <div className="gdai-label-header">
                <span className="fs-unmask">{`Example (${index + 1})`}</span>
                <RemoveExampleButton index={index} onRemove={onRemove} />
            </div>
            <div className="gdai-row">
                <TextAreaField
                    label="Example text"
                    placeholder="Example text"
                    value={example.text}
                    onChange={(text) => onChange({ text })}
                    height={EXAMPLE_TEXTAREA_HEIGHT}
                />
                <div className="gdai-field">
                    <FieldLabel
                        text={isMultilabel ? 'Labels' : 'Label'}
                        help={isMultilabel ? HELP_TEXTS.exampleLabels : HELP_TEXTS.exampleLabel}
                    />
                    {isMultilabel ? (
                        <DropdownMenu
                            selectType="multi-select"
                            size="medium"
                            fill
                            showLabel={false}
                            placeholder="Select labels"
                            tags={selectedLabels}
                            onTagRemove={(tag) =>
                                onChange({ labels: selectedLabels.filter((name) => name !== tag) })
                            }
                            value={selectedLabels}
                            items={labelNames.map((name) => ({
                                label: name,
                                type: 'checkbox',
                                checked: selectedLabels.includes(name),
                            }))}
                            onSelect={(item) =>
                                onChange({
                                    labels: selectedLabels.includes(item.label)
                                        ? selectedLabels.filter((name) => name !== item.label)
                                        : [...selectedLabels, item.label],
                                })
                            }
                        />
                    ) : (
                        <DropdownMenu
                            selectType="single-select"
                            size="medium"
                            fill
                            showLabel={false}
                            placeholder="Select label"
                            text={example.label || undefined}
                            value={example.label || undefined}
                            items={labelNames.map((name) => ({ label: name }))}
                            onSelect={(item) => onChange({ label: item.label })}
                            showClearButton
                            onClear={() => onChange({ label: '' })}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

ClassificationExampleRow.propTypes = {
    example: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    labelNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    isMultilabel: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

/** NER example: tag entity spans directly in the text (same editor as Generate with AI). */
const NerExampleRow = ({ example, index, labelNames, onChange, onRemove }) => (
    <div className="gdai-section">
        <div className="gdai-label-header">
            <span className="fs-unmask">{`Example (${index + 1})`}</span>
            <RemoveExampleButton index={index} onRemove={onRemove} />
        </div>
        <NerExampleEditor
            placeholder="Type or paste example text"
            text={example.text}
            entities={example.entities || []}
            entityNames={labelNames}
            onChange={onChange}
            height={110}
        />
    </div>
);

NerExampleRow.propTypes = {
    example: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    labelNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

/** Generative example: Input & output pair or Output only (Figma 5074:23748 / 5074:23814). */
const GenerativeExampleRow = ({ example, index, onChange, onRemove }) => {
    const mode = example.mode === 'output' ? 'output' : 'io';
    return (
        <div className="gdai-section">
            <div className="gdai-label-header">
                <div className="gdai-row">
                    <span className="fs-unmask">{`Example (${index + 1})`}</span>
                    <SegmentedControl
                        items={EXAMPLE_MODE_ITEMS}
                        activeId={mode}
                        onChange={(id) => onChange({ mode: id })}
                        size="small"
                    />
                </div>
                <RemoveExampleButton index={index} onRemove={onRemove} />
            </div>
            {mode === 'output' ? (
                <TextAreaField
                    label="Output Example"
                    placeholder="Output example"
                    value={example.text}
                    onChange={(text) => onChange({ text })}
                    height={EXAMPLE_TEXTAREA_HEIGHT}
                />
            ) : (
                <div className="gdai-row">
                    <TextAreaField
                        label="Input Text"
                        placeholder="Original Text"
                        value={example.inputText}
                        onChange={(inputText) => onChange({ inputText })}
                        height={EXAMPLE_TEXTAREA_HEIGHT}
                    />
                    <TextAreaField
                        label="Wanted Output"
                        placeholder="Wanted output"
                        value={example.text}
                        onChange={(text) => onChange({ text })}
                        height={EXAMPLE_TEXTAREA_HEIGHT}
                    />
                </div>
            )}
        </div>
    );
};

GenerativeExampleRow.propTypes = {
    example: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

const LabelBlock = ({ name, description, isNer, onDescriptionChange, onRemove }) => (
    <div className="gdai-section">
        <div className="gdai-label-header">
            <span className="fs-unmask">{name}</span>
            <button
                type="button"
                className="gdai-icon-button"
                aria-label={`Remove ${name}`}
                onClick={onRemove}
            >
                <SparkIcon slug="trash" size="small" />
            </button>
        </div>
        <TextAreaField
            label={isNer ? 'Entity description' : 'Label description'}
            help={isNer ? HELP_TEXTS.entityDescription : HELP_TEXTS.labelDescription}
            placeholder={isNer ? entityDescriptionPlaceholder(name) : LABEL_DESCRIPTION_PLACEHOLDER}
            value={description}
            onChange={onDescriptionChange}
            height={130}
        />
        {!isNer ? <p className="gdai-field-hint fs-unmask">Describe when this label should apply.</p> : null}
    </div>
);

LabelBlock.propTypes = {
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    isNer: PropTypes.bool,
    onDescriptionChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

const examplesHint = ({ isNer, isGenerative, isMultilabel }) => {
    if (isGenerative) return null;
    if (isNer) return NER_EXAMPLES_INSTRUCTION;
    return isMultilabel ? HELP_TEXTS.examplesMultilabel : HELP_TEXTS.examples;
};

const newExampleRow = ({ isGenerative }) => ({
    id: `ex-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    text: '',
    label: '',
    labels: [],
    entities: [],
    inputText: '',
    mode: isGenerative ? 'io' : undefined,
});

/**
 * Retrieval Agent step 2: per-label descriptions, task-specific examples,
 * and the record limit.
 */
const OptionalSettingsStep = ({
    showLabels,
    isNer,
    isGenerative,
    isMultilabel,
    labelNames,
    labelDescriptions,
    onLabelDescriptionChange,
    onRemoveLabel,
    examples,
    onExamplesChange,
    recordLimit,
    recordLimitErrorText = '',
    onRecordLimitChange,
}) => {
    const patchExample = (id, partial) =>
        onExamplesChange(examples.map((row) => (row.id === id ? { ...row, ...partial } : row)));
    const removeExample = (id) => onExamplesChange(examples.filter((row) => row.id !== id));

    const hint = examplesHint({ isNer, isGenerative, isMultilabel });

    return (
        <div className="gdai-section">
            <div>
                <div className="gdai-section-heading fs-unmask">Optional Settings</div>
                {isGenerative ? (
                    <p className="gdai-field-hint fs-unmask">{GENERATIVE_SETTINGS_HINT}</p>
                ) : null}
            </div>
            {showLabels
                ? labelNames.map((name) => (
                      <LabelBlock
                          key={name}
                          name={name}
                          description={labelDescriptions[name] || ''}
                          isNer={isNer}
                          onDescriptionChange={(value) => onLabelDescriptionChange(name, value)}
                          onRemove={() => onRemoveLabel(name)}
                      />
                  ))
                : null}
            <div className="gdai-section">
                <div>
                    <div className="gdai-section-heading fs-unmask">Examples</div>
                    {hint ? <p className="gdai-field-hint fs-unmask">{hint}</p> : null}
                </div>
                {examples.map((example, index) => {
                    const rowProps = {
                        example,
                        index,
                        onChange: (partial) => patchExample(example.id, partial),
                        onRemove: () => removeExample(example.id),
                    };
                    if (isGenerative) return <GenerativeExampleRow key={example.id} {...rowProps} />;
                    if (isNer) {
                        return <NerExampleRow key={example.id} {...rowProps} labelNames={labelNames} />;
                    }
                    return (
                        <ClassificationExampleRow
                            key={example.id}
                            {...rowProps}
                            labelNames={labelNames}
                            isMultilabel={isMultilabel}
                        />
                    );
                })}
                <div>
                    <button
                        type="button"
                        className="gdai-tiny-button"
                        onClick={() => onExamplesChange([...examples, newExampleRow({ isGenerative })])}
                    >
                        <SparkIcon slug="plus" size="small" />
                        <span className="fs-unmask">Add Example</span>
                    </button>
                </div>
            </div>
            <RecordCountInput
                label="Limit number of records"
                required
                help={HELP_TEXTS.recordLimit}
                value={recordLimit}
                onChange={onRecordLimitChange}
                errorText={recordLimitErrorText}
            />
        </div>
    );
};

OptionalSettingsStep.propTypes = {
    showLabels: PropTypes.bool,
    isNer: PropTypes.bool,
    isGenerative: PropTypes.bool,
    isMultilabel: PropTypes.bool,
    labelNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    labelDescriptions: PropTypes.object.isRequired,
    onLabelDescriptionChange: PropTypes.func.isRequired,
    onRemoveLabel: PropTypes.func.isRequired,
    examples: PropTypes.arrayOf(PropTypes.object).isRequired,
    onExamplesChange: PropTypes.func.isRequired,
    recordLimit: PropTypes.number,
    recordLimitErrorText: PropTypes.string,
    onRecordLimitChange: PropTypes.func.isRequired,
};

export default OptionalSettingsStep;
